import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { ArrowRight, Check } from 'lucide-react';

const packages = [
  {
    name: 'Digital Legacy Session',
    price: '$997',
    monthly: '$49',
    desc: 'For families who want this preserved — simply and digitally.',
    features: [
      'One guided legacy recording session',
      'Preservation of your parent\'s voice and stories',
      'Secure digital archive',
      'Heirloom Box with integrated video experience',
    ],
    highlight: false,
  },
  {
    name: 'Legacy Planning Intensive',
    price: '$1,497',
    monthly: '$72',
    desc: 'For those who want this done properly — once.',
    features: [
      'Legacy preparation session with a coordinator',
      'Professionally written life story book',
      'Life lessons preserved in your parent\'s own voice',
      'Enhanced physical presentation designed to be displayed',
    ],
    highlight: true,
    badge: 'Most Families Choose This',
  },
  {
    name: 'Heirloom Archive Collection',
    price: '$2,997',
    monthly: '$139',
    desc: 'For families who want a comprehensive, multi-generation archive.',
    features: [
      'Annual legacy update session (Year 1 included)',
      'Updated recordings and archive content',
      'Updated book editions delivered to the family',
      'Expanded materials for future generations',
    ],
    highlight: false,
  },
];

export default function Packages() {
  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO
        title="Legacy Planning Packages"
        path="/packages"
        description="Choose your legacy planning package: Digital Legacy Session ($997), Legacy Planning Intensive ($1,497), or Heirloom Archive Collection ($2,997). One-time, no subscriptions."
      />
      <Navbar />

      <section className="pt-32 sm:pt-40 pb-24 sm:pb-32">
        <div className="max-w-6xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16 sm:mb-20">
            <p className="overline mb-6">Legacy Planning Packages</p>
            <h1 data-testid="packages-title" className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl font-medium text-[#1C1917] leading-tight mb-6">
              Choose How You Want This Preserved
            </h1>
            <p className="text-base sm:text-lg text-[#57534E] leading-relaxed max-w-xl mx-auto">
              Each option reflects a different level of responsibility — not a different level of care.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6">
            {packages.map((pkg, i) => (
              <div
                key={i}
                data-testid={`package-${i}`}
                className={`relative p-8 sm:p-10 border transition-all duration-300 ${
                  pkg.highlight
                    ? 'border-[#1C1917] bg-[#1C1917] text-[#FCFBF9]'
                    : 'border-[#E7E5DF] bg-transparent hover:border-[#D6D3CD]'
                }`}
              >
                {pkg.badge && (
                  <span className="absolute -top-3 left-8 bg-[#A89F91] text-[#FCFBF9] text-xs font-semibold tracking-wider uppercase px-4 py-1">
                    {pkg.badge}
                  </span>
                )}
                <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium mb-2">
                  {pkg.name}
                </h3>
                <p className={`text-4xl sm:text-5xl font-['Cormorant_Garamond'] font-semibold mb-1 ${pkg.highlight ? 'text-[#FCFBF9]' : 'text-[#1C1917]'}`}>
                  {pkg.price}
                </p>
                <p className={`text-sm mb-6 ${pkg.highlight ? 'text-[#A89F91]/70' : 'text-[#A89F91]'}`}>
                  or as low as {pkg.monthly}/mo with <span className="font-semibold tracking-wide">Affirm</span>
                </p>
                <p className={`text-sm mb-8 ${pkg.highlight ? 'text-[#A89F91]' : 'text-[#57534E]'}`}>
                  {pkg.desc}
                </p>
                <div className="space-y-3 mb-10">
                  {pkg.features.map((f, j) => (
                    <div key={j} className="flex items-start gap-3">
                      <Check size={16} className={`mt-0.5 shrink-0 ${pkg.highlight ? 'text-[#A89F91]' : 'text-[#A89F91]'}`} />
                      <span className="text-sm">{f}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/contact"
                  data-testid={`package-cta-${i}`}
                  className={`flex items-center justify-center gap-2 py-3.5 text-sm font-medium tracking-wide transition-all duration-300 ${
                    pkg.highlight
                      ? 'bg-[#FCFBF9] text-[#1C1917] hover:bg-[#F2EFE9]'
                      : 'bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d]'
                  }`}
                >
                  Select {pkg.name} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>

          {/* Affirm Reassurance */}
          <div className="text-center mt-16 mb-4">
            <p className="font-['Cormorant_Garamond'] text-lg sm:text-xl text-[#57534E] leading-relaxed max-w-lg mx-auto mb-3">
              Families often choose monthly payments so they can preserve these memories now instead of waiting until it's too late.
            </p>
            <p className="text-sm text-[#A89F91]">
              Flexible monthly plans available through Affirm.
            </p>
          </div>

          <div className="w-12 h-[1px] bg-[#D6D3CD] mx-auto my-10" />

          <div className="text-center">
            <p className="font-['Cormorant_Garamond'] text-xl sm:text-2xl italic text-[#78716C]">
              This is a one-time experience. There are no subscriptions, no upsells, and no redo.
            </p>
          </div>

          <p className="text-[11px] text-[#A89F91]/70 text-center mt-8">
            Payment options through Affirm are subject to eligibility and approval.
          </p>
        </div>
      </section>

      {/* Expanded Legacy Options */}
      <section className="pb-24 sm:pb-32" data-testid="expanded-options-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          <div className="border-t border-[#E7E5DF] pt-20 sm:pt-24">
            <div className="text-center mb-16 sm:mb-20">
              <p className="overline mb-6">Expanded Legacy Options</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917] leading-tight mb-6">
                Tailored to what mattered most.
              </h2>
              <p className="text-sm sm:text-base text-[#78716C] leading-relaxed max-w-xl mx-auto">
                Many families also choose to include additional personalized keepsakes based on what mattered most to their loved one.
              </p>
            </div>

            {/* During Creation */}
            <div className="mb-16 sm:mb-20">
              <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#A89F91] mb-8">During Creation</p>
              <div className="space-y-0">
                {[
                  { title: 'Additional Family Member Interview', desc: 'Additional interviews with siblings, spouses, children, or close family members', price: '50% of selected package price' },
                  { title: 'Additional Printed Book Copies', desc: 'Professionally printed heirloom editions for extended family members', price: '$25 each' },
                  { title: 'Additional Heirloom Boxes', desc: 'Additional complete keepsake presentation boxes', price: '$150 each' },
                ].map((item, i) => (
                  <div key={i} data-testid={`option-during-${i}`} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-0 py-5 border-b border-[#E7E5DF]">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-['Cormorant_Garamond'] text-lg sm:text-xl font-medium text-[#1C1917]">{item.title}</h4>
                      <p className="text-sm text-[#A89F91] leading-relaxed mt-1">{item.desc}</p>
                    </div>
                    <p className="text-sm text-[#78716C] sm:text-right shrink-0 sm:ml-8 sm:w-48 font-['Cormorant_Garamond'] italic">{item.price}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* After Delivery */}
            <div className="mb-16 sm:mb-20">
              <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#A89F91] mb-8">After Delivery</p>
              <div className="space-y-0">
                {[
                  { title: 'Extended or Second Recording Session', desc: 'Additional recording sessions to continue preserving stories over time', price: '$397\u2013$497' },
                  { title: 'Additional Personalized Memorabilia', desc: 'Custom keepsakes and legacy pieces tailored to the individual', price: '$35\u2013$45 per item' },
                  { title: 'Book Edition Updates', desc: 'Updated printed editions with new stories, milestones, or family chapters', price: '$297 one-time' },
                ].map((item, i) => (
                  <div key={i} data-testid={`option-after-${i}`} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-0 py-5 border-b border-[#E7E5DF]">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-['Cormorant_Garamond'] text-lg sm:text-xl font-medium text-[#1C1917]">{item.title}</h4>
                      <p className="text-sm text-[#A89F91] leading-relaxed mt-1">{item.desc}</p>
                    </div>
                    <p className="text-sm text-[#78716C] sm:text-right shrink-0 sm:ml-8 sm:w-48 font-['Cormorant_Garamond'] italic">{item.price}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs text-[#A89F91] text-center">
              Available options vary depending on the story, materials, and family preferences.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
